/**
 * The word diff behind a correction card.
 *
 * Pure and DOM-free so it can be tested directly. Only the changed word is
 * highlighted (docs/05), and getting that wrong is expensive: marking a word
 * the learner got right tells them they made a mistake they did not make.
 */

export interface DiffPiece {
	text: string;
	changed: boolean;
}

/**
 * Splits `corrected` into pieces, flagging the ones that are not carried over
 * from `learner`. Whitespace is preserved as its own unchanged piece so the
 * caller can rebuild the line verbatim.
 */
export function diffCorrection(learner: string, corrected: string): DiffPiece[] {
	const pieces = corrected.split(/(\s+)/).filter((piece) => piece !== "");
	const learnerWords = learner.split(/\s+/).map(bareWord).filter((word) => word !== "");
	const correctedWords = pieces.filter((piece) => piece.trim() !== "").map(bareWord);
	const kept = commonSubsequence(learnerWords, correctedWords);

	let index = 0;
	return pieces.map((piece) => {
		if (piece.trim() === "") return { text: piece, changed: false };
		const changed = !kept.has(index);
		index += 1;
		return { text: piece, changed };
	});
}

/**
 * A word with its punctuation and case removed. Accents stay: `pao` and `pão`
 * are different words, and marking that fix is the point of the diff.
 */
export function bareWord(token: string): string {
	return token.toLowerCase().replace(/[.,!?;:¿¡"'()…]/g, "");
}

/**
 * Indices into `b` that survive from `a`, by longest common subsequence.
 *
 * Position matters. Asking only whether a word appears somewhere in the
 * learner's sentence gets both halves wrong: it marks every word ending in a
 * stop when punctuation is stripped from one side only, and it misses a real
 * fix like `do tomate` to `de tomate` whenever `de` happens to appear
 * elsewhere in the line.
 *
 * Sentences here are a dozen words, so the quadratic table costs nothing.
 */
export function commonSubsequence(a: string[], b: string[]): Set<number> {
	const table: number[][] = Array.from({ length: a.length + 1 }, () => new Array<number>(b.length + 1).fill(0));
	for (let i = a.length - 1; i >= 0; i -= 1) {
		for (let j = b.length - 1; j >= 0; j -= 1) {
			const row = table[i] ?? [];
			const next = table[i + 1] ?? [];
			row[j] = a[i] === b[j] ? (next[j + 1] ?? 0) + 1 : Math.max(next[j] ?? 0, row[j + 1] ?? 0);
		}
	}

	const kept = new Set<number>();
	let i = 0;
	let j = 0;
	while (i < a.length && j < b.length) {
		if (a[i] === b[j]) {
			kept.add(j);
			i += 1;
			j += 1;
		} else if ((table[i + 1]?.[j] ?? 0) >= (table[i]?.[j + 1] ?? 0)) {
			i += 1;
		} else {
			j += 1;
		}
	}
	return kept;
}
